import type {App, Plugin} from 'vue'
import type {Router} from 'vue-router'
import type {Store} from 'vuex'
import routes from './routes'
import mymachine from './store'
import axios from './axios'
import BIcon from '@/components/ui/BIcon.vue'
import OverlayWrapper from '@/components/ui/OverlayWrapper.vue'
import '@/assets/fonts/generated/bobcaticons.css'
import {library, dom} from '@fortawesome/fontawesome-svg-core'
import icons from './icons'
import debounce from 'lodash/debounce'
import constants from '@/constants'
import {setupPluginMixin} from './mixins/commonMixin'

library.add(icons)
dom.watch()

export interface PluginOptions {
  userContext?: string
  routerRoot?: string
  router: Router
  store: Store<any>
  getTokenFunction: () => Promise<string | false>
}

const defaultOptions: PluginOptions = {
  userContext: 'kags',
  routerRoot: '/',
  router: {} as Router,
  store: {} as Store<any>,
  getTokenFunction: async () => 'abc'
}

export function updateStore(state: any): void {
  const newState = {
    serials: state.mymachine.serials,
    language: state.mymachine.language,
    region: state.mymachine.region,
    organizations: state.mymachine.organizations,
    filteredOrganization: state.mymachine.filteredOrganization,
    filters: state.mymachine.filters
  }

  localStorage.setItem('mymachine_state', JSON.stringify(newState))
}

const MyMachinePlugin: Plugin = {
  install(app: App, options?: Partial<PluginOptions>) {
    const userOptions: PluginOptions = {...defaultOptions, ...options} as PluginOptions

    app.config.globalProperties.$userContext = userOptions.userContext

    // setup Authentication Bearer token
    axios.interceptors.request.use(
      async (config) => {
        const token = await userOptions.getTokenFunction()
        if (token) {
          if (!config.headers) config.headers = {}
          Object.assign(config.headers as Record<string, string>, {Authorization: `Bearer ${token}`})
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // replace root path for MyMachine main page
    routes[0].path = userOptions.routerRoot!
    for (const r of routes) {
      userOptions.router.addRoute(r)
    }

    userOptions.router.beforeEach(async (to, _from, next) => {
      if (to.path.includes('implicit/callback') || to.path.includes('/error')) {
        next()
        return
      }

      if (to.matched.some(record => record.meta.requiresAuth)) {
        const token = await userOptions.getTokenFunction()
        if (!token) {
          if (import.meta.env.VITE_OFFLINE === 'true') {
            next()
            return
          }

          const auth = app.config.globalProperties.$auth
          if (auth && !auth.isLoginRedirect?.()) {
            await auth.signInWithRedirect({originalUri: `${ window.location.pathname.replace(/^\/service/, '')}${window.location.search}`})
          }
          next(false)
          return
        }

        let features = userOptions.store.getters['mymachine/getFeatures']

        if (Object.keys(features).length === 0) {
          features = await Promise.race([
            new Promise(resolve => {
              const unwatchFeatures = userOptions.store.watch(
                store => store.mymachine.features,
                () => {
                  const nextFeatures = userOptions.store.getters['mymachine/getFeatures']
                  if (nextFeatures) {
                    resolve(nextFeatures)
                    unwatchFeatures()
                  }
                }
              )
            }),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Features load timeout')), 10000)
            )
          ]).catch(() => {
            next({name: 'Error', query: {error: 'featureLoadError'}})
            return null
          })
          if (!features) return
        }

        let region: string | null = userOptions.store.getters['mymachine/getRegion']
        const regionFromQuery = to.query[constants.REGION_QUERY_NAME]
        if (!region && typeof regionFromQuery === 'string') region = regionFromQuery

        if (!region) {
          region = await new Promise(resolve => {
            const checkRegion = () => {
              const currentRegion = userOptions.store.getters['mymachine/getRegion']
              if (currentRegion) {
                resolve(currentRegion)
                unwatchRegion()
                unwatchUser()
              }
            }

            const unwatchRegion = userOptions.store.watch(
              store => store.mymachine.region,
              checkRegion
            )
            const unwatchUser = userOptions.store.watch(
              store => store.mymachine.user,
              checkRegion
            )
          }) as string
        }

        const featureIsAllowed = !!(features as Record<string, boolean>)['mymachineApp' + region]
        featureIsAllowed ? next() : next({
          name: 'Error',
          query: {redirect: 'true', error: 'featureUnauthorized'}
        })
      } else {
        next()
      }
    })

    // add vuex module
    userOptions.store.registerModule('mymachine', mymachine)

    // restore previous state
    userOptions.store.commit('mymachine/restoreState')

    const debouncedUpdate = debounce(updateStore, 1000)

    // subscribe to save store on change
    userOptions.store.subscribe((_mutation, state) => {
      debouncedUpdate(state)
    })

    // setup global components
    app.component('b-icon', BIcon)
    app.component('overlay-wrapper', OverlayWrapper)

    // setup plugin mixin (global properties, filters, and methods)
    setupPluginMixin(app)
  }
}

export default MyMachinePlugin
